import React from "react";
import { motion } from "framer-motion";
import { Waves, Car, Coffee, Wifi } from "lucide-react";
import styles from "./AboutUs.module.css";
import { highlightGoldCoast } from "../../utils/highlightGoldCoast";
import HeroSection from "../HomePage/sections/HeroSection";

const AboutUs = () => {
  return (
    <div className={styles.pageContainer}>
      <HeroSection />

      {/* SECTION 1: Our Story (2-Row Layout) */}
      <section className={styles.storySection}>
        {/* Row 1: Entrance Circle + Grand Title */}
        <div className={styles.storyTopRow}>
          <motion.div
            className={styles.entranceCircle}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <img src="/images/ARB. 5.jpg" alt="Comfort" />
          </motion.div>
          <div className={styles.heroCopy}>
            <motion.h1
              className={styles.mainTitleText}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Experience Comfort in the Heart of Tweed Heads South, NSW
            </motion.h1>
            <motion.p
              className={styles.heroLead}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              Welcome to Arabella Motor Inn, your comfortable and convenient choice for Tweed Heads South, NSW accommodation in the beautiful Tweed Valley.
            </motion.p>
            <motion.p
              className={styles.heroLead}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.45 }}
            >
              Whether you're travelling for business, enjoying a weekend getaway or exploring the surrounding attractions, our motel in Tweed Heads South, NSW provides a welcoming place to relax and enjoy your stay.
            </motion.p>
          </div>
        </div>

        {/* Row 2: Our Story Text + Statue */}
        <div className={styles.storyBottomRow}>
          <motion.div
            className={styles.storyTextContent}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={styles.storyHeaderSmall}>Our Story</h2>
            <h3 className={styles.storySubheading}>A Convenient Place to Stay in Tweed Heads South, NSW</h3>
            <p className={styles.storyDescriptionFull}>
              At Arabella Motor Inn, our goal is to provide guests with comfortable accommodation and a convenient base from which to experience everything the local region has to offer.
            </p>
            <p className={styles.storyDescriptionFull}>
              Located in Tweed Heads South, NSW, our motel is surrounded by the natural beauty of the Tweed Valley, with local shops, dining, attractions and essential services close by.
            </p>
            <p className={styles.storyDescriptionFull}>
              Whether you're staying for one night or planning a longer visit, our motel accommodation in Tweed Heads South, NSW gives you the opportunity to relax while exploring the region.
            </p>
            <p className={styles.storyDescriptionFull}>
              From discovering things to do near Tweed Heads South, NSW to visiting beaches, national parks, wildlife attractions and local towns, there is plenty to experience during your stay.
            </p>
          </motion.div>

          <motion.div
            className={styles.storyStatueWrapper}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <img src="/images/buddhasstatus.png" className={styles.statueImgFixed} alt="Peaceful Details" />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: Hero Section (Coastal Charm) */}
      <section
        className={styles.charmHero}
        style={{ backgroundImage: 'url("/images/ARB. 7.jpg")' }}
      >
        <motion.div
          className={styles.charmText}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>Modern Comfort. <br />Coastal Charm Awaits</h2>
        </motion.div>
      </section>

      {/* SECTION 3: Our Amenities */}
      <section className={styles.amenitiesSection}>
        <div className={styles.amenitiesHeader}>
          <h2>Convenient Amenities Near Tweed Heads South, NSW &amp; Tweed Heads</h2>
          <p>
            Enjoy convenient amenities at Arabella Motor Inn, providing comfortable Tweed Heads South, NSW accommodation for travellers exploring Tweed Heads South, NSW, Tweed Heads and the surrounding Tweed Valley. Relax by the swimming pool, enjoy convenient on-site parking and unwind in our peaceful courtyard while staying close to local attractions, shops and places to visit.
          </p>
        </div>

        <div className={styles.amenitiesGrid}>
          <motion.div
            className={styles.amenityCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5 }}
          >
            <div className={styles.amenityCircle}>
              <img src="/images/Arb. 1.jpg" alt="Swimming pool" />
            </div>
            <div className={styles.amenityLabel}>
              <Waves size={22} /> Relaxing Swimming Pool
            </div>
            <p className={styles.amenityDesc}>
              Refresh and unwind in our swimming pool after a day exploring Tweed Heads South, NSW, Tweed Heads and the Tweed Valley.
            </p>
          </motion.div>

          <motion.div
            className={styles.amenityCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className={styles.amenityCircle}>
              <img src="/images/img2.jpg" alt="On-site parking" />
            </div>
            <div className={styles.amenityLabel}>
              <Car size={22} /> Convenient On-Site Parking
            </div>
            <p className={styles.amenityDesc}>
              Enjoy convenient on-site parking while using Arabella Motor Inn as your base for exploring Tweed Heads South, NSW and nearby Tweed Heads.
            </p>
          </motion.div>

          <motion.div
            className={styles.amenityCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className={styles.amenityCircle}>
              <img src="/images/img1.jpg" alt="Peaceful courtyard" />
            </div>
            <div className={styles.amenityLabel}>
              <Coffee size={22} /> Peaceful Courtyard
            </div>
            <p className={styles.amenityDesc}>
              Take some time to relax in our peaceful courtyard after discovering local Tweed Heads South, NSW attractions and things to do near Tweed Heads.
            </p>
          </motion.div>

          <motion.div
            className={styles.amenityCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <div className={styles.amenityCircle}>
              <img src="/images/ARB. 10.jpg" alt="Free Wi-Fi" />
            </div>
            <div className={styles.amenityLabel}>
              <Wifi size={22} /> Free Wi-Fi (if available)
            </div>
            <p className={styles.amenityDesc}>
              Stay connected throughout your visit with convenient Wi-Fi at our motel in Tweed Heads South, NSW, whether you're travelling for business or leisure.
            </p>
          </motion.div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <a href="https://book-directonline.com/properties/southtweedmidirect" target="_blank" rel="noreferrer" className={styles.bookBtn}>
            Book Your Serene Escape
          </a>
        </div>
      </section>

      {/* SECTION 4: Local Adventures (Staff) */}
      <section className={styles.localAdventures}>
        <div
          className={styles.localVisualWrapper}
          style={{ backgroundImage: 'url("/images/ARB. 3.jpg")' }}
        />
        <motion.div
          className={styles.localTextWrapper}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>Local Adventures</h2>
          <p>
            {highlightGoldCoast(
              "Modern comfort in local hospitality. We are located at the heart of Tweed Heads, providing you with the best of both worlds. Explore delightful local cafes, surf spots, and much more."
            )}
          </p>
          <p>
            {highlightGoldCoast(
              "Our team is dedicated to making your coastal escape truly memorable with personalized recommendations and authentic local soul. The property is non-smoking, with daily room service and a team that can help with local Tweed Heads South, NSW directions."
            )}
          </p>
          <p>
            {highlightGoldCoast(
              "Whether the stay is a night between flights, a family visit, or a few days on the coast, this Tweed Heads motel keeps the focus on a well-kept room and an easy Tweed Heads South, NSW location."
            )}
          </p>
        </motion.div>
      </section>

    </div>
  );
};

export default AboutUs;
